import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, ShieldAlert, Database } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SCOPES = [
  { key: 'fatture_emesse', label: 'Fatture emesse (FAT-A)', warn: 'Cancella tutte le fatture verso clienti' },
  { key: 'note_credito_emesse', label: 'Note credito emesse (NCR-A)', warn: 'Cancella tutte le NC verso clienti' },
  { key: 'fatture_ricevute', label: 'Fatture ricevute (FAT-P)', warn: 'Cancella tutte le fatture dei fornitori' },
  { key: 'note_credito_ricevute', label: 'Note credito ricevute (NCR-P)', warn: 'Cancella tutte le NC dei fornitori' },
  { key: 'prima_nota', label: 'Prima Nota', warn: 'Cancella tutti i movimenti di cassa/banca' },
  { key: 'movimenti_finanziari', label: 'Movimenti finanziari di cantiere', warn: 'Cancella entrate/uscite agganciate ai cantieri' },
  { key: 'spese_materiali', label: 'Spese materiali (DDT registrati in cantiere)', warn: 'Cancella i materiali contabilizzati nei cantieri' },
  { key: 'pagamenti_scadenze', label: 'Pagamenti / Scadenze separate', warn: 'Cancella record di pagamento separati se presenti' },
];

export default function ResetContabilitaPage() {
  const [scopes, setScopes] = useState(Object.fromEntries(SCOPES.map(s => [s.key, false])));
  const [masterKey, setMasterKey] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const toggle = (k) => setScopes(prev => ({ ...prev, [k]: !prev[k] }));
  const selectAll = (v) => setScopes(Object.fromEntries(SCOPES.map(s => [s.key, v])));
  const selectedCount = Object.values(scopes).filter(Boolean).length;

  const handleSubmit = async () => {
    if (selectedCount === 0) { toast.error('Seleziona almeno un ambito'); return; }
    if (!masterKey) { toast.error('Chiave master obbligatoria'); return; }
    if (confirmation !== 'RESET') { toast.error('Scrivi RESET per confermare'); return; }
    setBusy(true);
    setLastResult(null);
    try {
      const r = await axios.post(`${API}/admin/reset-contabilita`,
        { master_key: masterKey, ...scopes },
        { withCredentials: true }
      );
      toast.success(r.data.message);
      setLastResult(r.data);
      // Reset form
      setScopes(Object.fromEntries(SCOPES.map(s => [s.key, false])));
      setMasterKey('');
      setConfirmation('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore reset');
    } finally {
      setBusy(false);
    }
  };

  const handleResetAll = async () => {
    if (!masterKey) { toast.error('Chiave master obbligatoria'); return; }
    if (confirmation !== 'RESET') { toast.error('Scrivi RESET per confermare'); return; }
    if (!window.confirm('Confermi RESET COMPLETO? Verranno cancellati TUTTI i dati di fatturazione e contabilità (fatture, NC, prima nota, movimenti, spese). Operazione IRREVERSIBILE.')) return;
    setBusy(true);
    setLastResult(null);
    try {
      const r = await axios.post(`${API}/admin/reset-contabilita`,
        { master_key: masterKey, reset_all: true },
        { withCredentials: true }
      );
      toast.success(r.data.message);
      setLastResult(r.data);
      setScopes(Object.fromEntries(SCOPES.map(s => [s.key, false])));
      setMasterKey('');
      setConfirmation('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore reset');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2"><ShieldAlert className="h-7 w-7 text-red-600" />Reset dati contabilità</h1>
        <p className="text-slate-500 mt-1">Operazione distruttiva e irreversibile. Esegui un export/backup prima di procedere.</p>
      </div>

      <Card className="border-amber-300 bg-amber-50">
        <CardContent className="p-4 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 space-y-1">
            <p><b>Partitario</b> e <b>Registri IVA</b> non hanno storage proprio: sono viste calcolate al volo dalle fatture. Cancellando le fatture, queste sezioni si svuotano in automatico.</p>
            <p>L'anagrafica clienti/fornitori, i cantieri e i rapportini <b>NON vengono toccati</b> da questo reset.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base">
            <span>Ambiti da resettare</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => selectAll(true)} data-testid="select-all">Seleziona tutto</Button>
              <Button size="sm" variant="outline" onClick={() => selectAll(false)} data-testid="deselect-all">Deseleziona</Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {SCOPES.map(s => (
            <label key={s.key} className="flex items-start gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
              <Checkbox checked={scopes[s.key]} onCheckedChange={() => toggle(s.key)} className="mt-0.5" data-testid={`scope-${s.key}`} />
              <div className="flex-1">
                <div className="font-medium text-sm">{s.label}</div>
                <div className="text-xs text-slate-500">{s.warn}</div>
              </div>
            </label>
          ))}
        </CardContent>
      </Card>

      <Card className="border-red-300">
        <CardHeader><CardTitle className="text-base text-red-700">Conferma operazione</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Chiave master di sicurezza</Label>
            <Input
              type="password"
              value={masterKey}
              onChange={(e) => setMasterKey(e.target.value)}
              placeholder="CR-RESET-2024"
              data-testid="master-key"
            />
            <p className="text-xs text-slate-500 mt-1">La chiave è quella usata per il reset password admin di emergenza.</p>
          </div>
          <div>
            <Label>Scrivi RESET in maiuscolo per confermare</Label>
            <Input
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder="RESET"
              data-testid="confirm-reset"
            />
          </div>
          <Button
            onClick={handleSubmit}
            disabled={busy || selectedCount === 0 || confirmation !== 'RESET' || !masterKey}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="submit-reset"
          >
            <Database className="h-4 w-4 mr-2" />
            {busy ? 'Reset in corso…' : `Esegui reset (${selectedCount} ambit${selectedCount === 1 ? 'o' : 'i'})`}
          </Button>

          <div className="pt-4 border-t border-red-200">
            <p className="text-xs text-slate-500 mb-2">Oppure cancella tutto in 1 click (ignora la selezione sopra):</p>
            <Button
              onClick={handleResetAll}
              disabled={busy || confirmation !== 'RESET' || !masterKey}
              variant="outline"
              className="border-red-600 text-red-700 hover:bg-red-50 w-full"
              data-testid="reset-all-btn"
            >
              <ShieldAlert className="h-4 w-4 mr-2" />
              RESET COMPLETO — tutto in cascata
            </Button>
          </div>
        </CardContent>
      </Card>

      {lastResult && (
        <Card className="border-emerald-300 bg-emerald-50">
          <CardHeader><CardTitle className="text-base text-emerald-800">Reset completato</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-emerald-900 mb-2">{lastResult.message}</p>
            <div className="text-xs space-y-1">
              {Object.entries(lastResult.deleted || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-emerald-200 py-0.5">
                  <span>{k}</span><span className="font-mono">{v} record</span>
                </div>
              ))}
            </div>
            {lastResult.note && (
              <p className="text-xs text-emerald-700 italic mt-3">{lastResult.note}</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
